import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faFilter, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import FilterDropdown from '../../../../../../../../Components/FilterDropdown/FilterDropdown';
import { TaskByEmployee } from '@frontend/Models/Tasks/TaskModels';
import './CompactFilterBar.css';

interface CompactFilterBarProps {
  showMyTasks: boolean;
  onToggleTasks: (showMyTasks: boolean) => void;
  currentStatusFilter: string;
  onStatusFilter: (status: string) => void;
  statusCounts: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
  };
  tasks: TaskByEmployee[];
  searchTerm?: string;
  onSearch?: (searchTerm: string) => void;
  onFilterClick?: () => void;
  matterFilter?: string;
  onMatterFilterChange?: (value: string) => void;
  areaFilter?: string;
  onAreaFilterChange?: (value: string) => void;
  scopeFilter?: string;
  onScopeFilterChange?: (value: string) => void;
  priorityFilter?: string;
  onPriorityFilterChange?: (value: string) => void;
  onAddTask?: () => void;
}

const CompactFilterBar: React.FC<CompactFilterBarProps> = ({
  showMyTasks,
  onToggleTasks,
  currentStatusFilter,
  onStatusFilter,
  statusCounts,
  tasks,
  searchTerm: externalSearchTerm = '',
  onSearch,
  onFilterClick,
  matterFilter: externalMatterFilter = 'all',
  onMatterFilterChange,
  areaFilter: externalAreaFilter = 'all',
  onAreaFilterChange,
  scopeFilter: externalScopeFilter = 'all',
  onScopeFilterChange,
  priorityFilter: externalPriorityFilter = 'all',
  onPriorityFilterChange,
  onAddTask,
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>(externalAreaFilter);
  const [scopeFilter, setScopeFilter] = useState<string>(externalScopeFilter);
  const [assigneeFilter, setAssigneeFilter] = useState<string>(externalPriorityFilter);
  const [searchTerm, setSearchTerm] = useState<string>(externalSearchTerm);

  // Sync with external values
  React.useEffect(() => {
    setTypeFilter(externalAreaFilter);
  }, [externalAreaFilter]);

  React.useEffect(() => {
    setScopeFilter(externalScopeFilter);
  }, [externalScopeFilter]);

  React.useEffect(() => {
    setAssigneeFilter(externalPriorityFilter);
  }, [externalPriorityFilter]);

  React.useEffect(() => {
    setSearchTerm(externalSearchTerm);
  }, [externalSearchTerm]);

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const taskTypes = Array.from(new Set(tasks.map((t) => t.task_type).filter(Boolean)));
    const assignees = Array.from(new Set(tasks.map((t) => t.assignedto).filter(Boolean)));
    
    return {
      taskTypes: taskTypes.map((t) => ({ value: t || '', label: t || '' })),
      assignees: assignees.map((a) => ({ value: a || '', label: a || '' })),
      scopes: [
        { value: 'all', label: 'All Scopes' },
        { value: 'Internal', label: 'Internal' },
        { value: 'External', label: 'External' },
      ],
    };
  }, [tasks]);

  const handleTypeChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    setTypeFilter(val);
    if (onAreaFilterChange) {
      onAreaFilterChange(val);
    }
  };

  const handleScopeChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    setScopeFilter(val);
    if (onScopeFilterChange) {
      onScopeFilterChange(val);
    }
  };

  const handleAssigneeChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    setAssigneeFilter(val);
    if (onPriorityFilterChange) {
      onPriorityFilterChange(val);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(searchTerm);
    }
  };

  return (
    <div className="compact-filter-bar">
      <div className="compact-filter-bar-main">
        {/* Toggle Tabs */}
        <div className="compact-filter-tabs">
          <button
            className={`compact-filter-tab ${showMyTasks ? 'active' : ''}`}
            onClick={() => onToggleTasks(true)}
          >
            My Tasks
          </button>
          <button
            className={`compact-filter-tab ${!showMyTasks ? 'active' : ''}`}
            onClick={() => onToggleTasks(false)}
          >
            Team's Tasks
          </button>
        </div>

        {/* Filter Toggle Button */}
        <div className="compact-filter-actions">
          {onAddTask && (
            <button
              type="button"
              className="compact-filter-add-task"
              onClick={onAddTask}
              aria-label="Add task"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="compact-filter-add-task-text">Add Task</span>
            </button>
          )}
        <button
          className="compact-filter-toggle"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          aria-label={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          <FontAwesomeIcon icon={faFilter} />
          <span className="compact-filter-toggle-text">Filters</span>
          <FontAwesomeIcon
            icon={isFilterExpanded ? faChevronUp : faChevronDown}
            className="compact-filter-chevron"
          />
        </button>
        </div>
      </div>

      {/* Collapsible Filter Bar */}
      {isFilterExpanded && (
        <div className="compact-filter-expanded">
          <div className="compact-filter-row">
            <div className="compact-filter-dropdowns">
              <FilterDropdown
                label=""
                placeholder="All Types"
                options={[
                  { value: 'all', label: 'All Types' },
                  ...filterOptions.taskTypes,
                ]}
                value={typeFilter}
                onChange={handleTypeChange}
                className="compact-filter-dropdown"
              />
              <FilterDropdown
                label=""
                placeholder="All Scopes"
                options={filterOptions.scopes}
                value={scopeFilter}
                onChange={handleScopeChange}
                className="compact-filter-dropdown"
              />
              <FilterDropdown
                label=""
                placeholder="Assigned To"
                options={[
                  { value: 'all', label: 'All Assignees' },
                  ...filterOptions.assignees,
                ]}
                value={assigneeFilter}
                onChange={handleAssigneeChange}
                className="compact-filter-dropdown"
              />
            </div>
            <div className="compact-filter-search-container">
              <div className="compact-filter-search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="compact-filter-search-icon" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  className="compact-filter-search-input"
                />
              </div>
              <button
                className="compact-filter-button"
                onClick={onFilterClick}
                aria-label="Filter options"
              >
                <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompactFilterBar;

